// import { getAllRooms } from '@/app/services/rooms/rooms';
import { Divider } from '@material-ui/core';
import { useState, useEffect } from 'react';
import FilterIcon from '@shared/icons/FilterIcon';

import PopoverCombobox from 'shared/components/PopoverCombobox';
import { getMyCompanies } from '@/app/services/Company/companyService';
import { getMySchools, getRoomsBySchool } from '@/app/services/schools/schools';
import TableTooltip from 'shared/Reports/TableTooltip';

const CameraListingFiltersBar = ({ filters, setFilters }) => {
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [companiesLoading, setCompaniesLoading] = useState(false);
	const [companiesLoadingMore, setCompaniesLoadingMore] = useState(false);
	const [companies, setCompanies] = useState([]);
	const [companyPage, setCompanyPage] = useState(1);
	const [hasMoreCompanies, setHasMoreCompanies] = useState(true);
	const [currentCompanySearch, setCurrentCompanySearch] = useState('');

	const [schoolsLoading, setSchoolsLoading] = useState(false);
	const [schoolsLoadingMore, setSchoolsLoadingMore] = useState(false);
	const [schools, setSchools] = useState([]);
	const [schoolPage, setSchoolPage] = useState(1);
	const [hasMoreSchools, setHasMoreSchools] = useState(true);
	const [currentSchoolSearch, setCurrentSchoolSearch] = useState('');

	const [roomsLoading, setRoomsLoading] = useState(false);
	const [rooms, setRooms] = useState([]);

	const handleSearchCompanies = async (searchTerm) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeoutId = setTimeout(async () => {
			setCompaniesLoading(true);
			setCompanyPage(1);
			setCurrentCompanySearch(searchTerm);
			try {
				const res = await getMyCompanies(1, false, searchTerm);
				setCompanies(res.data.data.map((company) => ({ label: company.name, value: company.id })));
				setHasMoreCompanies(res.data.meta.current_page < res.data.meta.last_page);
			} catch (error) {
				console.error('Failed to fetch companies', error);
			}
			setCompaniesLoading(false);
		}, 300);

		setSearchTimeout(timeoutId);
	};

	const handleLoadMoreCompanies = async () => {
		if (companiesLoadingMore) return;

		setCompaniesLoadingMore(true);
		try {
			const nextPage = companyPage + 1;
			const res = await getMyCompanies(nextPage, false, currentCompanySearch);
			const newCompanies = res.data.data.map((company) => ({ label: company.name, value: company.id }));

			setCompanies([...companies, ...newCompanies]);
			setCompanyPage(nextPage);
			setHasMoreCompanies(nextPage < res.data.meta.last_page);
		} catch (error) {
			console.error('Failed to load more companies', error);
		}
		setCompaniesLoadingMore(false);
	};

	const handleSearchSchools = async (searchTerm) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeoutId = setTimeout(async () => {
			setSchoolsLoading(true);
			setSchoolPage(1);
			setCurrentSchoolSearch(searchTerm);
			try {
				const res = await getMySchools(searchTerm, 1);
				setSchools(res.data.data.map((school) => ({ label: school.name, value: school.id })));
				setHasMoreSchools(res.data.meta.current_page < res.data.meta.last_page);
			} catch (error) {
				console.error('Failed to fetch schools', error);
			}
			setSchoolsLoading(false);
		}, 300);

		setSearchTimeout(timeoutId);
	};

	const handleLoadMoreSchools = async () => {
		if (schoolsLoadingMore) return;

		setSchoolsLoadingMore(true);
		try {
			const nextPage = schoolPage + 1;
			const res = await getMySchools(currentSchoolSearch, nextPage);
			const newSchools = res.data.data.map((school) => ({ label: school.name, value: school.id }));

			setSchools([...schools, ...newSchools]);
			setSchoolPage(nextPage);
			setHasMoreSchools(nextPage < res.data.meta.last_page);
		} catch (error) {
			console.error('Failed to load more schools', error);
		}
		setSchoolsLoadingMore(false);
	};

	const handleGetRooms = async (schoolIds) => {
		if (!schoolIds?.length) {
			setRooms([]);
			return;
		}

		setRoomsLoading(true);
		try {
			// Fetch rooms for all selected schools in parallel
			// We need to do this because more than one school can be selected
			const roomPromises = schoolIds.map((school) => getRoomsBySchool(school.value));
			const responses = await Promise.all(roomPromises);

			// Combine and map all room results
			const allRooms = responses.flatMap((res) =>
				res.data.map((room) => {
					return {
						label: room.name,
						value: room.id,
					};
				})
			);

			setRooms(allRooms);
		} catch (error) {
			console.error('Failed to fetch rooms', error);
			setRooms([]);
		}
		setRoomsLoading(false);
	};

	// Load rooms when school filter changes
	useEffect(() => {
		handleGetRooms(filters.school_id);
	}, [filters.school_id]);

	return (
		<div
			className="text-white py-16 px-16 md:px-32 justify-between flex flex-wrap"
			style={{ background: 'rgb(0, 46,108)' }}
		>
			<div className="flex items-center gap-8 text-16">
				<FilterIcon /> <span>Filter:</span>
			</div>
			<div className="flex gap-8 md:gap-16 flex-wrap">
				<PopoverCombobox
					currentFilter="company_id"
					filters={filters}
					hasMore={hasMoreCompanies}
					isInfiniteScroll={true}
					loading={companiesLoading}
					loadingMore={companiesLoadingMore}
					onLoadMore={handleLoadMoreCompanies}
					onSearch={handleSearchCompanies}
					options={companies}
					setFilter={setFilters}
					title="Company"
				/>
				<Divider className="bg-white" flexItem orientation="vertical" />
				<PopoverCombobox
					currentFilter="school_id"
					filters={filters}
					hasMore={hasMoreSchools}
					isInfiniteScroll={true}
					loading={schoolsLoading}
					loadingMore={schoolsLoadingMore}
					onLoadMore={handleLoadMoreSchools}
					onSearch={handleSearchSchools}
					options={schools}
					setFilter={setFilters}
					title="School"
				/>
				<Divider className="bg-white" flexItem orientation="vertical" />
				{!filters?.school_id?.length ? (
					<TableTooltip title="Please select a school to filter by room name">
						<div className="">
							<PopoverCombobox
								currentFilter=""
								disabled
								filters={filters}
								loading={roomsLoading}
								options={[{ label: 'No School Selected', value: null }]}
								setFilter={setFilters}
								title="Room Name"
							/>
						</div>
					</TableTooltip>
				) : (
					<PopoverCombobox
						currentFilter="room_id"
						filters={filters}
						loading={roomsLoading}
						options={rooms}
						setFilter={setFilters}
						title="Room Name"
					/>
				)}
			</div>
		</div>
	);
};

export default CameraListingFiltersBar;
